import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Settings, User, Bell, Shield, CreditCard, Link as LinkIcon, Eye, EyeOff, Save, Crown, ExternalLink, AtSign, Check, X, Loader2, Clock, Lock, Radio, Camera, MessageCircle, Heart, Share2, Tag, Sparkles, AlertCircle, CheckCircle, Trash2, UserPlus, Palette, Moon, Sun, Monitor } from 'lucide-react';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { SocialSyncSettings } from '../components/SocialSyncSettings';
import ProfilePhotoUploader from '../components/ProfilePhotoUploader';
import { PhotoGallery } from '../components/PhotoGallery';
import { BannerUpload } from '../components/BannerUpload';
import { BackgroundSettingsPanel } from '../components/ProfileBackground';
import { API } from '../config/api';
import { getTierDisplayName, formatRemainingComments } from '../utils/permissions';
import { checkHandleAvailability, safeJson } from '../utils/safeJson';
import { useTheme } from '../context/ThemeContext';
import { usePushNotifications } from '../hooks/usePushNotifications';



// Handle Settings Component
function HandleSettings({ user, refreshUser }) {
  const { token } = useAuth();
  const [newHandle, setNewHandle] = useState('');
  const [checking, setChecking] = useState(false);
  const [changing, setChanging] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const checkAvailabilityDebounced = useCallback((value) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const clean = value.toLowerCase().replace('@', '');
    if (clean.length < 3 || clean === user?.handle) {
      setAvailability(null);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();
      setChecking(true);
      
      try {
        const result = await checkHandleAvailability(
          clean,
          API,
          abortControllerRef.current.signal
        );
        
        if (result.error !== 'aborted') {
          setAvailability(result);
        }
      } catch (error) {
        console.error('Error checking handle:', error);
        setAvailability({
          available: false,
          reason: "Can't verify handle right now",
          handle: clean
        });
      } finally {
        setChecking(false);
      }
    }, 400);
  }, [user?.handle]);

  const handleInputChange = (e) => {
    let value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setNewHandle(value);
    setAvailability(null);
    if (value.length >= 3 && value !== user?.handle) {
      checkAvailabilityDebounced(value);
    }
  };

  const handleChangeHandle = async () => {
    if (!newHandle || !availability?.available) return;
    setChanging(true);
    try {
      const res = await fetch(`${API}/handle/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ handle: newHandle })
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success(`Handle changed to @${newHandle}`);
        setShowChangeForm(false);
        setNewHandle('');
        if (refreshUser) await refreshUser();
      } else {
        toast.error(result.error || 'Failed to change handle');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setChanging(false);
    }
  };

  const canChange = user?.can_change_handle !== false;
  const daysRemaining = user?.days_until_handle_change || 0;

  return (
    <div className="pt-4 border-t border-border">
      <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
        <AtSign className="w-5 h-5 text-primary" />
        Your Handle
      </h3>
      
      {user?.handle ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="text-2xl font-bold text-primary">{user.handle ? `@${user.handle}` : ""}</p>
              <p className="text-sm text-muted-foreground">Your unique handle on DMV Soundstage</p>
            </div>
            {canChange ? (
              <Button 
                variant="outline" 
                onClick={() => setShowChangeForm(!showChangeForm)}
              >
                Change Handle
              </Button>
            ) : (
              <div className="text-right">
                <Badge variant="outline" className="mb-1">
                  <Clock className="w-3 h-3 mr-1" />
                  {daysRemaining} days until change
                </Badge>
                <p className="text-xs text-muted-foreground">Handle can be changed once per 30 days</p>
              </div>
            )}
          </div>

          {showChangeForm && canChange && (
            <div className="p-4 bg-muted/20 rounded-lg space-y-3">
              <Label>New Handle</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                <Input
                  value={newHandle}
                  onChange={handleInputChange}
                  placeholder="newhandle"
                  className="pl-8"
                  maxLength={20}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {!checking && availability?.available && <Check className="w-4 h-4 text-green-500" />}
                  {!checking && availability && !availability.available && <X className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              {availability && (
                <p className={`text-sm ${availability.available ? 'text-green-500' : 'text-red-500'}`}>
                  {availability.reason}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  onClick={handleChangeHandle}
                  disabled={!availability?.available || changing}
                >
                  {changing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save New Handle
                </Button>
                <Button variant="ghost" onClick={() => setShowChangeForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-400 font-medium">You haven't set a handle yet!</p>
          <p className="text-sm text-muted-foreground mt-1">
            Go to <a href="/claim-handle" className="text-primary hover:underline">claim your handle</a> to set up your unique @handle.
          </p>
        </div>
      )}
    </div>
  );
}

// Appearance / Theme Settings Component
function AppearanceSettings() {
  const { theme, setTheme, themes } = useTheme();

  const themeCards = [
    {
      id: 'dark',
      icon: Moon,
      label: 'Dark',
      description: 'Pure dark mode',
      preview: { bg: '#0B0B0E', card: '#111116', text: '#F5F5F5', border: '#222230' }
    },
    {
      id: 'gray',
      icon: Monitor,
      label: 'Gray',
      description: 'Softer dark with gray tones',
      preview: { bg: '#1C1C21', card: '#272730', text: '#F0F0F0', border: '#333340' }
    },
    {
      id: 'light',
      icon: Sun,
      label: 'Light',
      description: 'Clean light mode',
      preview: { bg: '#FAFAFA', card: '#FFFFFF', text: '#1a1a2e', border: '#E0E0E5' }
    }
  ];

  return (
    <Card className="glass-card border-border p-6" data-testid="appearance-settings">
      <h2 className="font-heading text-xl font-bold uppercase mb-2">Appearance</h2>
      <p className="text-sm text-muted-foreground mb-6">Choose your preferred background mode</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {themeCards.map((t) => {
          const isActive = theme === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`theme-${t.id}-btn`}
              onClick={() => {
                setTheme(t.id);
                toast.success(`Switched to ${t.label} mode`);
              }}
              className={`relative rounded-xl p-4 text-left transition-all duration-200 border-2 ${
                isActive
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-border hover:border-muted-foreground/30'
              }`}
              style={{ background: 'hsl(var(--card))' }}
            >
              {/* Mini preview */}
              <div
                className="rounded-lg mb-3 p-3 border"
                style={{ background: t.preview.bg, borderColor: t.preview.border }}
              >
                <div className="space-y-1.5">
                  <div className="h-2 w-3/4 rounded" style={{ background: t.preview.text, opacity: 0.7 }} />
                  <div className="h-2 w-1/2 rounded" style={{ background: t.preview.text, opacity: 0.4 }} />
                </div>
                <div className="flex gap-1.5 mt-2">
                  <div className="h-6 w-6 rounded" style={{ background: t.preview.card, border: `1px solid ${t.preview.border}` }} />
                  <div className="h-6 flex-1 rounded" style={{ background: t.preview.card, border: `1px solid ${t.preview.border}` }} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <span className="font-semibold text-sm">{t.label}</span>
                {isActive && <Check className="w-4 h-4 text-primary ml-auto" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, isAuthenticated, getAuthHeaders, isArtist, hasActiveSubscription, isOwner, refreshUser, userRole, logout, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permissions, setPermissions] = useState(null);
  
  // Delete account modal state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Push notifications
  const { permission: pushPermission, isSubscribed: pushSubscribed, supported: pushSupported, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications(token);
  
  // Account settings
  const [accountSettings, setAccountSettings] = useState({
    name: '',
    email: '',
    bio: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    in_app: true,
    email: false,
    sms: false,
    digest_mode: 'instant',
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    categories: {
      content: true,
      live: true,
      social: true,
      engagement: true,
      system: true,
    }
  });
  const [notifLoaded, setNotifLoaded] = useState(false);
  
  // Privacy settings
  const [privacy, setPrivacy] = useState({
    profilePublic: true,
    showPlays: true,
    showFollowers: true,
    allowMessages: true
  });
  
  // Sharing settings
  const [shareSettings, setShareSettings] = useState({
    allow_profile_sharing: true,
    allow_post_sharing: true,
    allow_track_sharing: true,
    allow_photo_sharing: true,
    allow_video_sharing: true,
    allow_replay_sharing: true,
  });
  
  // Follow requests state
  const [followRequests, setFollowRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsCount, setRequestsCount] = useState(0);
  
  // Social links (for artists)
  const [socials, setSocials] = useState({
    instagram: '',
    youtube: '',
    twitter: '',
    tiktok: '',
    soundcloud: '',
    spotify: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [isAuthenticated]);

  const fetchSettings = async () => {
    try {
      // Get user data
      if (user) {
        setAccountSettings(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || '',
          bio: user.bio || ''
        }));
        
        // Load socials from user data if available
        if (user.socials && Object.keys(user.socials).length > 0) {
          setSocials(user.socials);
        } else if (user.links && Object.keys(user.links).length > 0) {
          setSocials(user.links);
        }
      }

      // Get current profile privacy setting
      try {
        const privacyRes = await fetch(`${API}/me/privacy/profile`, {
          headers: getAuthHeaders()
        });
        if (privacyRes.ok) {
          const privacyData = await privacyRes.json();
          setPrivacy(prev => ({
            ...prev,
            profilePublic: !privacyData.is_private
          }));
        }
      } catch (err) {
        console.error('Failed to fetch privacy settings:', err);
      }

      // Fetch share settings
      try {
        const shareRes = await fetch(`${API}/me/share-settings`, {
          headers: getAuthHeaders()
        });
        if (shareRes.ok) {
          const shareData = await shareRes.json();
          setShareSettings(prev => ({ ...prev, ...(shareData.share_settings || {}) }));
        }
      } catch (err) {
        console.error('Failed to fetch share settings:', err);
      }

      // Get pending follow requests count
      try {
        const requestsRes = await fetch(`${API}/me/follow-requests/count`, {
          headers: getAuthHeaders()
        });
        if (requestsRes.ok) {
          const data = await requestsRes.json();
          setRequestsCount(data.count);
        }
      } catch (err) {
        console.error('Failed to fetch follow requests count:', err);
      }

      // Get permissions
      try {
        const permRes = await fetch(`${API}/me/permissions`, {
          headers: getAuthHeaders()
        });
        if (permRes.ok) {
          setPermissions(await permRes.json());
        }
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
      }

      // Get subscription if artist
      if (hasActiveSubscription || isOwner) {
        const subRes = await fetch(`${API}/subscriptions/my-subscription`, {
          headers: getAuthHeaders()
        });
        if (subRes.ok) {
          setSubscription(await subRes.json());
        }
      }

      // Get artist profile if artist
      if (isArtist && user?.user_id) {
        const artistsRes = await fetch(`${API}/artists`);
        if (artistsRes.ok) {
          const artists = await artistsRes.json();
          const myProfile = artists.find(a => a.user_id === user.user_id);
          if (myProfile?.socials) {
            setSocials(myProfile.socials);
          }
        }
      }

      // Get notification preferences
      try {
        const notifRes = await fetch(`${API}/settings/notifications`, {
          headers: getAuthHeaders()
        });
        if (notifRes.ok) {
          const data = await notifRes.json();
          if (data.preferences) {
            setNotifications(prev => ({...prev, ...data.preferences, categories: {...prev.categories, ...(data.preferences.categories || {})}}));
          }
        }
        setNotifLoaded(true);
      } catch (err) {
        console.error('Failed to fetch notification preferences:', err);
        setNotifLoaded(true);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (accountSettings.newPassword && accountSettings.newPassword !== accountSettings.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      // Update profile (name, bio)
      const profileResponse = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          name: accountSettings.name,
          bio: accountSettings.bio
        })
      });
      
      if (!profileResponse.ok) {
        const error = await profileResponse.json();
        toast.error(error.detail || 'Failed to update profile');
        return;
      }
      
      // Update password if provided
      if (accountSettings.newPassword) {
        const passwordResponse = await fetch(`${API}/users/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            current_password: accountSettings.currentPassword,
            new_password: accountSettings.newPassword
          })
        });

        if (!passwordResponse.ok) {
          let errorMsg = 'Failed to update password';
          try {
            const error = await passwordResponse.json();
            errorMsg = error.detail || error.message || errorMsg;
          } catch (e) {
            const text = await passwordResponse.text();
            if (text.includes('<!DOCTYPE') || text.includes('<html')) {
              errorMsg = 'Server temporarily unavailable. Please try again.';
            }
          }
          toast.error(errorMsg);
          return;
        }
      }

      toast.success('Account settings updated');
      setAccountSettings(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Settings update error:', error);
      toast.error('Network error - please check your connection and try again');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNotifications = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings/notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(notifications)
      });

      if (response.ok) {
        toast.success('Notification preferences saved');
      } else {
        toast.error('Failed to update notifications');
      }
    } catch (error) {
      toast.error('Failed to update notifications');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrivacy = async () => {
    setSaving(true);
    try {
      // Save privacy settings
      const response = await fetch(`${API}/users/privacy`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(privacy)
      });

      // Save share settings
      const shareRes = await fetch(`${API}/me/share-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(shareSettings)
      });

      if (response.ok && shareRes.ok) {
        toast.success('Privacy & sharing settings saved');
      } else {
        toast.error('Failed to update some settings');
      }
    } catch (error) {
      toast.error('Failed to update privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePrivateProfile = async (isPrivate) => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        toast.error('Session expired - please log in again');
        return;
      }
      
      const response = await fetch(`${API}/me/privacy/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ is_private: isPrivate })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Profile is now ${isPrivate ? 'private' : 'public'}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || errorData.message || 'Failed to update privacy settings';
        console.error('Privacy update error:', errorData);
        toast.error(errorMsg);
        // Revert the toggle
        setPrivacy(prev => ({ ...prev, profilePublic: isPrivate }));
      }
    } catch (error) {
      console.error('Privacy update network error:', error);
      toast.error('Network error - please check your connection');
      // Revert the toggle
      setPrivacy(prev => ({ ...prev, profilePublic: isPrivate }));
    }
  };

  const fetchFollowRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await fetch(`${API}/me/follow-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setFollowRequests(data.requests);
        setRequestsCount(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch follow requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await fetch(`${API}/follow-requests/${requestId}/accept`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Follow request accepted');
        // Remove from list
        setFollowRequests(prev => prev.filter(r => r.request_id !== requestId));
        setRequestsCount(prev => prev - 1);
      } else {
        toast.error('Failed to accept request');
      }
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await fetch(`${API}/follow-requests/${requestId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Follow request rejected');
        // Remove from list
        setFollowRequests(prev => prev.filter(r => r.request_id !== requestId));
        setRequestsCount(prev => prev - 1);
      } else {
        toast.error('Failed to reject request');
      }
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleUpdateSocials = async () => {
    setSaving(true);
    try {
      // Use the new auth/profile endpoint that supports all users
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ socials })
      });

      if (response.ok) {
        toast.success('Social links updated');
        if (refreshUser) refreshUser();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update social links');
      }
    } catch (error) {
      toast.error('Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error('Failed to access subscription portal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="settings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold uppercase mb-2 flex items-center gap-3">
            <Settings className="w-8 h-8 text-primary" />
            Settings
          </h1>
          <p className="text-muted-foreground">Manage your account preferences</p>
        </div>

        <Tabs defaultValue="account">
          <TabsList className="bg-muted/50 mb-8 flex-wrap">
            <TabsTrigger value="account">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="appearance" data-testid="settings-appearance-tab">
              <Palette className="w-4 h-4 mr-2" />
              Appearance
            </TabsTrigger>
            <TabsTrigger value="privacy">
              <Shield className="w-4 h-4 mr-2" />
              Privacy
            </TabsTrigger>
            <TabsTrigger value="security">
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="permissions">
              <Shield className="w-4 h-4 mr-2" />
              My Permissions
            </TabsTrigger>
            {/* Social Sync - for Streamers and Podcasters */}
            {(userRole === 'streamer' || userRole === 'podcaster' || isOwner) && (
              <TabsTrigger value="socialsync">
                <Radio className="w-4 h-4 mr-2" />
                Social Sync
              </TabsTrigger>
            )}
            {(isArtist || hasActiveSubscription || isOwner) && (
              <>
                <TabsTrigger value="subscription">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription
                </TabsTrigger>
                <TabsTrigger value="socials">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Social Links
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card className="glass-card border-border p-6">
              <h2 className="font-heading text-xl font-bold uppercase mb-6">Account Information</h2>
              
              <div className="space-y-6">
                {/* Profile Photo Section with Quick Uploader */}
                <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-border">
                  <ProfilePhotoUploader
                    currentImage={user?.picture}
                    onUploadSuccess={(newUrl) => {
                      if (refreshUser) refreshUser();
                      toast.success('Profile photo updated everywhere!');
                    }}
                    getAuthHeaders={getAuthHeaders}
                    user={user}
                    size="lg"
                  />
                  <div className="text-center sm:text-left">
                    <h3 className="font-medium mb-1">Profile Photo</h3>
                    <p className="text-sm text-muted-foreground">
                      This photo appears on your profile, comments, posts, and everywhere you interact.
                    </p>
                  </div>
                </div>

                {/* Full Photo Gallery */}
                <div className="pb-6 border-b border-border">
                  <PhotoGallery
                    currentAvatarUrl={user?.avatar_url || user?.picture}
                    getAuthHeaders={getAuthHeaders}
                    onAvatarChange={(newUrl) => {
                      if (refreshUser) refreshUser();
                    }}
                  />
                </div>

                {/* Banner & Background Upload Section - Artists Only */}
                {(isArtist || isOwner) && (
                  <div className="pb-6 border-b border-border">
                    <BannerUpload
                      currentBannerUrl={user?.banner_url}
                      artistName={user?.name}
                      onUploadSuccess={() => {
                        if (refreshUser) refreshUser();
                      }}
                      getAuthHeaders={getAuthHeaders}
                    />
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input
                      value={accountSettings.name}
                      onChange={(e) => setAccountSettings({ ...accountSettings, name: e.target.value })}
                      placeholder="Your name"
                      data-testid="settings-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      value={accountSettings.email}
                      disabled
                      className="bg-muted/30"
                    />
                    <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                  </div>
                </div>
                
                {/* Bio Section */}
                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <textarea
                    id="bio"
                    value={accountSettings.bio}
                    onChange={(e) => setAccountSettings({ ...accountSettings, bio: e.target.value.slice(0, 500) })}
                    placeholder="Tell people about yourself..."
                    className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                    maxLength={500}
                  />
                  <p className="text-xs text-muted-foreground text-right">{accountSettings.bio.length}/500</p>
                </div>

                {/* Handle Section */}
                <HandleSettings user={user} refreshUser={refreshUser} />

                <div className="pt-4 border-t border-border">
                  <h3 className="font-heading text-lg font-bold mb-4">Change Password</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={accountSettings.currentPassword}
                          onChange={(e) => setAccountSettings({ ...accountSettings, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={accountSettings.newPassword}
                        onChange={(e) => setAccountSettings({ ...accountSettings, newPassword: e.target.value })}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={accountSettings.confirmPassword}
                        onChange={(e) => setAccountSettings({ ...accountSettings, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="outline">{user?.role}</Badge>
                    {isOwner && <Badge className="bg-purple-500/20 text-purple-400">Founder</Badge>}
                  </div>
                  <Button onClick={handleUpdateAccount} disabled={saving} data-testid="save-account-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>

                {/* Profile Mode Selector */}
                <div className="pt-4 border-t border-border">
                  <label className="block text-sm font-medium mb-2">Profile Display Mode</label>
                  <p className="text-xs text-muted-foreground mb-2">Choose how your profile page appears to visitors. Content Creator mode shows a video-first, influencer-style layout.</p>
                  <select
                    value={user?.profile_mode || user?.role || 'listener'}
                    onChange={async (e) => {
                      try {
                        const res = await fetch(`${API}/content-creator/profile-mode`, {
                          method: 'PATCH',
                          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                          body: JSON.stringify({ profile_mode: e.target.value })
                        });
                        if (res.ok) {
                          toast.success(`Profile mode set to ${e.target.value.replace('_', ' ')}`);
                          window.location.reload();
                        }
                      } catch { toast.error('Failed to update profile mode'); }
                    }}
                    className="bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm w-full sm:w-auto"
                    data-testid="profile-mode-select"
                  >
                    <option value="artist">Artist (Music-first)</option>
                    <option value="content_creator">Content Creator (Video-first)</option>
                    <option value="podcaster">Podcaster</option>
                    <option value="streamer">Streamer</option>
                    <option value="listener">Listener</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Danger Zone - Delete Account */}
            <Card className="glass-card border-red-500/30 p-6 mt-6">
              <h2 className="font-heading text-xl font-bold uppercase mb-4 text-red-400">Danger Zone</h2>
              
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-red-400">Delete Account</h3>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                    onClick={() => setShowDeleteAccountModal(true)}
                    data-testid="delete-account-btn"
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="glass-card border-border p-6">
              <h2 className="font-heading text-xl font-bold uppercase mb-6" data-testid="notification-settings-title">Notification Preferences</h2>
              
              <div className="space-y-8">
                {/* Delivery Channels */}
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Delivery Channels</h3>
                  
                  <div className="flex items-center justify-between" data-testid="notif-in-app-toggle">
                    <div>
                      <p className="font-medium">In-App Notifications</p>
                      <p className="text-sm text-muted-foreground">Bell icon alerts within the platform</p>
                    </div>
                    <Switch
                      checked={notifications.in_app}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, in_app: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between" data-testid="notif-email-toggle">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-muted-foreground">Receive emails when people you follow post new content</p>
                    </div>
                    <Switch
                      checked={notifications.email}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between" data-testid="notif-sms-toggle">
                    <div>
                      <p className="font-medium">SMS/Text Notifications</p>
                      <p className="text-sm text-muted-foreground">Text alerts for important activity (requires verified phone)</p>
                    </div>
                    <Switch
                      checked={notifications.sms}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, sms: checked })}
                    />
                  </div>

                  {/* Push Notifications */}
                  {pushSupported && (
                    <div className="flex items-center justify-between" data-testid="notif-push-toggle">
                      <div>
                        <p className="font-medium">Push Notifications</p>
                        <p className="text-sm text-muted-foreground">
                          {pushPermission === 'denied'
                            ? 'Blocked by browser — enable in your browser settings'
                            : 'Get notified of calls & messages even when the app is closed'}
                        </p>
                      </div>
                      <Switch
                        checked={pushSubscribed}
                        disabled={pushPermission === 'denied'}
                        onCheckedChange={async (checked) => {
                          if (checked) {
                            const ok = await pushSubscribe();
                            if (ok) toast.success('Push notifications enabled');
                            else if (pushPermission !== 'denied') toast.error('Could not enable push notifications');
                          } else {
                            await pushUnsubscribe();
                            toast.success('Push notifications disabled');
                          }
                        }}
                      />
                    </div>
                  )}

                  {notifications.email && (
                    <div className="pl-4 border-l-2 border-orange-500/30" data-testid="notif-digest-mode">
                      <p className="font-medium text-sm mb-2">Email Digest Mode</p>
                      <div className="flex gap-2 flex-wrap">
                        {['instant', 'hourly', 'daily'].map(mode => (
                          <button
                            key={mode}
                            onClick={() => setNotifications({ ...notifications, digest_mode: mode })}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              notifications.digest_mode === mode
                                ? 'bg-orange-500 text-white'
                                : 'bg-muted/30 hover:bg-muted/50 text-muted-foreground'
                            }`}
                          >
                            {mode.charAt(0).toUpperCase() + mode.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Category Toggles */}
                <div className="space-y-4 border-t border-border pt-6">
                  <h3 className="font-medium text-lg">What to Notify</h3>
                  
                  {[
                    { key: 'content', label: 'New Content', desc: 'Posts, tracks, videos, clips from people you follow' },
                    { key: 'live', label: 'Live Activity', desc: 'When someone goes live or posts a replay' },
                    { key: 'social', label: 'Social', desc: 'New followers, follow requests' },
                    { key: 'engagement', label: 'Engagement', desc: 'Likes, comments, replies, reposts' },
                    { key: 'system', label: 'System & Announcements', desc: 'Platform updates, milestones, warnings' },
                  ].map(cat => (
                    <div key={cat.key} className="flex items-center justify-between" data-testid={`notif-cat-${cat.key}`}>
                      <div>
                        <p className="font-medium">{cat.label}</p>
                        <p className="text-sm text-muted-foreground">{cat.desc}</p>
                      </div>
                      <Switch
                        checked={notifications.categories?.[cat.key] ?? true}
                        onCheckedChange={(checked) => setNotifications({
                          ...notifications,
                          categories: { ...notifications.categories, [cat.key]: checked }
                        })}
                      />
                    </div>
                  ))}
                </div>

                {/* Quiet Hours */}
                <div className="space-y-4 border-t border-border pt-6">
                  <div className="flex items-center justify-between" data-testid="notif-quiet-hours">
                    <div>
                      <h3 className="font-medium text-lg">Quiet Hours</h3>
                      <p className="text-sm text-muted-foreground">Pause email/SMS during set hours</p>
                    </div>
                    <Switch
                      checked={notifications.quiet_hours_enabled}
                      onCheckedChange={(checked) => setNotifications({ ...notifications, quiet_hours_enabled: checked })}
                    />
                  </div>
                  {notifications.quiet_hours_enabled && (
                    <div className="flex gap-4 pl-4 border-l-2 border-orange-500/30">
                      <div>
                        <label className="text-xs text-muted-foreground">Start</label>
                        <input
                          type="time"
                          value={notifications.quiet_hours_start}
                          onChange={e => setNotifications({ ...notifications, quiet_hours_start: e.target.value })}
                          className="block mt-1 bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">End</label>
                        <input
                          type="time"
                          value={notifications.quiet_hours_end}
                          onChange={e => setNotifications({ ...notifications, quiet_hours_end: e.target.value })}
                          className="block mt-1 bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button onClick={handleUpdateNotifications} disabled={saving} data-testid="save-notification-prefs-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Save Preferences
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance">
            <AppearanceSettings />
            <Card className="glass-card border-border p-6 mt-4" data-testid="profile-background-settings">
              <h2 className="font-heading text-xl font-bold uppercase mb-2">Profile Background</h2>
              <p className="text-sm text-muted-foreground mb-5">Customize your profile page background</p>
              <BackgroundSettingsPanel />
            </Card>
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="privacy">
            <Card className="glass-card border-border p-6">
              <h2 className="font-heading text-xl font-bold uppercase mb-6">Privacy Settings</h2>
              
              <div className="space-y-6">
                {/* NEW: Profile Privacy Toggle - Primary Setting */}
                <div className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-lg">Private Account</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          When enabled, only your followers can see your posts, music, videos, and clips.
                          Your profile header, bio, and follower count will still be visible.
                        </p>
                      </div>
                      <Switch
                        checked={!privacy.profilePublic}
                        onCheckedChange={(checked) => {
                          setPrivacy({ ...privacy, profilePublic: !checked });
                          // Also update the new is_private field via API
                          handleTogglePrivateProfile(!checked ? false : true);
                        }}
                        data-testid="private-profile-toggle"
                      />
                    </div>
                    {!privacy.profilePublic && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <p className="text-sm text-amber-400">
                          🔒 Your account is currently private. Only approved followers can see your content.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Follow Requests Section - Only show for private accounts */}
                  {!privacy.profilePublic && (
                    <div className="p-4 bg-muted/30 rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <UserPlus className="w-5 h-5 text-secondary" />
                          <p className="font-medium text-lg">Follow Requests</p>
                          {requestsCount > 0 && (
                            <Badge variant="secondary" className="ml-2">{requestsCount}</Badge>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={fetchFollowRequests}
                          disabled={loadingRequests}
                        >
                          {loadingRequests ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                        </Button>
                      </div>
                      
                      {followRequests.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No pending follow requests
                        </p>
                      ) : (
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {followRequests.map((request) => (
                            <div 
                              key={request.request_id} 
                              className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                              data-testid={`follow-request-${request.request_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-muted overflow-hidden">
                                  {request.requester?.avatar_url ? (
                                    <img 
                                      src={request.requester.avatar_url} 
                                      alt={request.requester?.name} 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                                      <User className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">{request.requester?.name || 'Unknown'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    @{request.requester?.handle || request.requester_id}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleRejectRequest(request.request_id)}
                                  data-testid={`reject-request-${request.request_id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => handleAcceptRequest(request.request_id)}
                                  className="bg-secondary hover:bg-secondary/80"
                                  data-testid={`accept-request-${request.request_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-t border-border pt-4">
                    <h3 className="font-medium mb-3 text-muted-foreground">Additional Privacy Options</h3>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Show Play Count</p>
                      <p className="text-sm text-muted-foreground">Display total plays on your tracks</p>
                    </div>
                    <Switch
                      checked={privacy.showPlays}
                      onCheckedChange={(checked) => setPrivacy({ ...privacy, showPlays: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Show Follower Count</p>
                      <p className="text-sm text-muted-foreground">Display your follower count publicly</p>
                    </div>
                    <Switch
                      checked={privacy.showFollowers}
                      onCheckedChange={(checked) => setPrivacy({ ...privacy, showFollowers: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Allow Messages</p>
                      <p className="text-sm text-muted-foreground">Let other users send you messages</p>
                    </div>
                    <Switch
                      checked={privacy.allowMessages}
                      onCheckedChange={(checked) => setPrivacy({ ...privacy, allowMessages: checked })}
                    />
                  </div>

                  {/* Sharing Settings */}
                  <div className="border-t border-border pt-4">
                    <h3 className="font-medium mb-1 text-muted-foreground">Sharing Controls</h3>
                    <p className="text-xs text-muted-foreground mb-4">Choose which content types others can share from your profile</p>
                  </div>

                  {[
                    { key: 'allow_profile_sharing', label: 'Profile', desc: 'Allow others to share your profile link' },
                    { key: 'allow_post_sharing', label: 'Posts', desc: 'Allow sharing of your posts' },
                    { key: 'allow_track_sharing', label: 'Tracks / Songs', desc: 'Allow sharing of your music' },
                    { key: 'allow_photo_sharing', label: 'Photos', desc: 'Allow sharing of your photos' },
                    { key: 'allow_video_sharing', label: 'Videos', desc: 'Allow sharing of your videos' },
                    { key: 'allow_replay_sharing', label: 'Live Replays', desc: 'Allow sharing of your live stream replays' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={shareSettings[item.key] !== false}
                        onCheckedChange={(checked) => setShareSettings(prev => ({ ...prev, [item.key]: checked }))}
                        data-testid={`share-toggle-${item.key}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button onClick={handleUpdatePrivacy} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Privacy Settings
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <TwoFactorSettings />
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions">
            <Card className="glass-card border-border p-6">
              <h2 className="font-heading text-xl font-bold uppercase mb-6">My Permissions</h2>
              
              {permissions ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Permission Level</p>
                        <p className="font-medium text-lg">{permissions.summary}</p>
                      </div>
                      <Badge variant={permissions.isFullTier ? "default" : "secondary"} className="text-sm">
                        {permissions.tierName || 'Free'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Permission Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Like Permission */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-pink-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">Like Content</h4>
                          <p className="text-xs text-muted-foreground">Like posts, reels, and tracks</p>
                        </div>
                        {permissions.like?.allowed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {permissions.like?.allowed ? 'Unlimited' : 'Not available'}
                      </p>
                    </div>
                    
                    {/* Share Permission */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Share2 className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">Share Content</h4>
                          <p className="text-xs text-muted-foreground">Share posts and reels</p>
                        </div>
                        {permissions.share?.allowed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {permissions.share?.allowed ? 'Unlimited' : 'Not available'}
                      </p>
                    </div>
                    
                    {/* Comment Permission */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <MessageCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">Comment</h4>
                          <p className="text-xs text-muted-foreground">Comment on posts and reels</p>
                        </div>
                        {permissions.comment?.allowed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      {permissions.comment?.rate_limited ? (
                        <div className="space-y-1">
                          <p className="text-sm text-amber-500">
                            {permissions.comment?.remaining !== undefined 
                              ? formatRemainingComments(permissions.comment.remaining, permissions.comment.limit_per_hour)
                              : `Limited to ${permissions.comment.limit_per_hour}/hour`}
                          </p>
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="p-0 h-auto text-primary"
                            onClick={() => navigate('/subscription')}
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            Upgrade for unlimited
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {permissions.comment?.allowed ? 'Unlimited' : permissions.comment?.reason || 'Not available'}
                        </p>
                      )}
                    </div>
                    
                    {/* Tag Permission */}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Tag className="w-5 h-5 text-purple-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">Tag Users</h4>
                          <p className="text-xs text-muted-foreground">Tag others in posts and comments</p>
                        </div>
                        {permissions.tag?.allowed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {permissions.tag?.unlimited 
                          ? 'Unlimited - tag anyone' 
                          : permissions.tag?.requires_mutual_follow 
                            ? 'Mutual followers only' 
                            : 'Not available'}
                      </p>
                      {permissions.tag?.requires_mutual_follow && !permissions.tag?.unlimited && (
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0 h-auto text-primary mt-1"
                          onClick={() => navigate('/subscription')}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          Upgrade to tag anyone
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Role & Tier Info */}
                  <div className="border-t border-border pt-4 mt-4">
                    <h4 className="font-medium mb-3">Account Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Role</p>
                        <p className="capitalize">{permissions.role || 'Listener'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Tier</p>
                        <p>{permissions.tierName || 'Free'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Social Sync Tab */}
          <TabsContent value="socialsync">
            <SocialSyncSettings />
          </TabsContent>

          {/* Subscription Tab */}
          {(isArtist || hasActiveSubscription || isOwner) && (
            <TabsContent value="subscription">
              <Card className="glass-card border-border p-6">
                <h2 className="font-heading text-xl font-bold uppercase mb-6">Subscription Status</h2>
                
                {isOwner ? (
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Crown className="w-8 h-8 text-purple-400" />
                      <div>
                        <h3 className="font-heading text-xl font-bold">Founder Access</h3>
                        <p className="text-purple-400">Owner (All Access)</p>
                      </div>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>✓ Unlimited uploads</li>
                      <li>✓ Unlimited channels</li>
                      <li>✓ All features unlocked</li>
                      <li>✓ 0% platform fee</li>
                      <li>✓ Billing exempt</li>
                    </ul>
                  </div>
                ) : subscription?.active ? (
                  <div className="space-y-6">
                    <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-heading text-xl font-bold">{subscription.tier_name}</h3>
                          <p className="text-secondary">${subscription.price}/month</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Track Limit</p>
                          <p className="font-medium">{subscription.track_limit === -1 ? 'Unlimited' : subscription.track_limit}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Channels</p>
                          <p className="font-medium">{subscription.channel_limit === -1 ? 'Unlimited' : subscription.channel_limit}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Platform Fee</p>
                          <p className="font-medium">{subscription.platform_fee_percent}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Started</p>
                          <p className="font-medium">{subscription.started_at ? new Date(subscription.started_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={handleManageSubscription}>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Manage Subscription
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/pricing')}>
                        View All Plans
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="font-heading text-xl font-bold mb-2">No Active Subscription</h3>
                    <p className="text-muted-foreground mb-6">Subscribe to unlock artist features</p>
                    <Button onClick={() => navigate('/pricing')}>
                      View Subscription Plans
                    </Button>
                  </div>
                )}
              </Card>
            </TabsContent>
          )}

          {/* Social Links Tab */}
          {(isArtist || hasActiveSubscription || isOwner) && (
            <TabsContent value="socials">
              <Card className="glass-card border-border p-6">
                <h2 className="font-heading text-xl font-bold uppercase mb-6">Social Media Links</h2>
                
                <div className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Connect your social profiles. Instagram and YouTube are required.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        Instagram <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={socials.instagram || ''}
                        onChange={(e) => setSocials({ ...socials, instagram: e.target.value })}
                        placeholder="https://instagram.com/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        YouTube <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={socials.youtube || ''}
                        onChange={(e) => setSocials({ ...socials, youtube: e.target.value })}
                        placeholder="https://youtube.com/@channel"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Twitter/X</Label>
                      <Input
                        value={socials.twitter || ''}
                        onChange={(e) => setSocials({ ...socials, twitter: e.target.value })}
                        placeholder="https://twitter.com/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TikTok</Label>
                      <Input
                        value={socials.tiktok || ''}
                        onChange={(e) => setSocials({ ...socials, tiktok: e.target.value })}
                        placeholder="https://tiktok.com/@username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SoundCloud</Label>
                      <Input
                        value={socials.soundcloud || ''}
                        onChange={(e) => setSocials({ ...socials, soundcloud: e.target.value })}
                        placeholder="https://soundcloud.com/username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Spotify</Label>
                      <Input
                        value={socials.spotify || ''}
                        onChange={(e) => setSocials({ ...socials, spotify: e.target.value })}
                        placeholder="https://open.spotify.com/artist/..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-border">
                    <Button onClick={handleUpdateSocials} disabled={saving}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Social Links
                    </Button>
                  </div>
                </div>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Delete Account Modal */}
      {showDeleteAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4">
          <Card className="glass-card border-red-500/30 max-w-md w-full p-6">
            <h2 className="font-heading text-xl font-bold uppercase text-red-400 mb-4">
              Delete Your Account
            </h2>
            
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-300 font-medium mb-2">
                  ⚠️ Warning: This action is permanent and cannot be undone.
                </p>
                <p className="text-sm text-muted-foreground">
                  All your data will be permanently deleted:
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                  <li>Your profile and account information</li>
                  <li>All your posts, reels, and stories</li>
                  <li>All your tracks and videos (if artist)</li>
                  <li>Your followers and following lists</li>
                  <li>Any wallet balance and transactions</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-confirm" className="text-sm">
                  Type <span className="text-red-400 font-bold">DELETE</span> to confirm:
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type DELETE"
                  className="border-red-500/30"
                  data-testid="delete-confirm-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDeleteAccountModal(false);
                    setDeleteConfirmation('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={deleteConfirmation !== 'DELETE' || deleting}
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      const res = await fetch(`${API}/account`, {
                        method: 'DELETE',
                        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ confirmation: 'DELETE' })
                      });
                      
                      if (res.ok) {
                        toast.success('Your account has been deleted.');
                        logout();
                        navigate('/');
                      } else {
                        const data = await res.json();
                        toast.error(data.detail || 'Failed to delete account');
                      }
                    } catch (err) {
                      console.error('Delete account error:', err);
                      toast.error('Failed to delete account');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete My Account'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
